import type { NodeJson, FlattenedNodeJson } from "@corca/data";

type Predicate<T> = (child: T) => boolean;
type Node = NodeJson | FlattenedNodeJson;

export function findChild<
  T extends NodeJson["type"],
  N1 extends NodeJson | FlattenedNodeJson,
  N2 = N1 extends FlattenedNodeJson ? FlattenedNodeJson : NodeJson,
>(
  node: N1,
  predicate: T | Predicate<N2>,
  nodeMap: Map<string, FlattenedNodeJson>,
): T extends string ? Extract<N2, { type: T }> | undefined : N2 | undefined;

export function findChild(
  node: Node,
  predicate: string | Predicate<Node>,
  nodeMap: Map<string, FlattenedNodeJson>,
): Node | undefined {
  if ("children" in node) {
    return node.children.find(
      typeof predicate === "string" ? (c) => c.type === predicate : predicate,
    );
  }

  const childId = node.childrenIds.find((id) => {
    const child = nodeMap.get(id);
    return child && (typeof predicate === "string" ? child.type === predicate : predicate(child));
  });
  return childId ? nodeMap.get(childId) : undefined;
}

export function filterChildren<
  N1 extends NodeJson | FlattenedNodeJson,
  N2 = N1 extends FlattenedNodeJson ? FlattenedNodeJson : NodeJson,
>(node: N1, predicate: Predicate<N2>, nodeMap: Map<string, FlattenedNodeJson>): N2[];

export function filterChildren(
  node: Node,
  predicate: Predicate<Node>,
  nodeMap: Map<string, FlattenedNodeJson>,
): Node[] {
  if ("children" in node) {
    return node.children.filter(predicate);
  }

  return node.childrenIds
    .map((id) => nodeMap.get(id))
    .filter((child) => !!child && predicate(child)) as Node[];
}

export function mapChildren<
  N1 extends NodeJson | FlattenedNodeJson,
  N2 = N1 extends FlattenedNodeJson ? FlattenedNodeJson : NodeJson,
  T = unknown,
>(node: N1, mapper: (child: N2) => T, nodeMap: Map<string, FlattenedNodeJson>): T[];

export function mapChildren(
  node: Node,
  mapper: (child: Node) => Node,
  nodeMap: Map<string, FlattenedNodeJson>,
): Node[] {
  if ("children" in node) {
    return node.children.map(mapper);
  }

  return node.childrenIds
    .map((id) => nodeMap.get(id))
    .filter((child) => !!child)
    .map(mapper);
}
